/**
 * iter307b — Shared sort utility for the admin Enrollment lists.
 *
 * Hoisted out of EnrollmentTable.jsx so the parent Enrollments page
 * can sort the FULL filtered list before pagination slicing.
 * Previously the sort lived inside EnrollmentTable which only sees one
 * page at a time → each page sorted independently, no global ordering.
 *
 * Returns a NEW array; never mutates the input.
 */
export const ENROLLMENT_DATE_FIELDS = new Set([
  'approved_at', 'start_date', 'pause_date', 'created_at',
  'end_date', 'next_billing_date', 'last_payment_date',
  'auto_resume_date', 'cancel_date', 'scheduled_cancel_date',
  'payment_due_date', 'updated_at', 'paid_at',
]);

// iter307b-fix — The backend `/admin/enrollments` response already
// serializes every date field with the EXACT name the UI columns use
// (`cancel_date`, `payment_due_date`, `next_billing_date`, etc.) — no
// aliasing needed. The iter307 alias map below was a misfire that
// silently rerouted sorts to non-existent row keys, making the
// Cancelled + Due Date columns no-op when clicked.
export const ENROLLMENT_FIELD_ALIAS = {};

const NUMERIC_FIELDS = new Set([
  'monthly_fee', 'prorated_amount', 'credit_balance',
  'numeric_id', 'player_numeric_id', 'parent_numeric_id',
]);

export function sortEnrollments(rows, sortField, sortDirection = 'desc') {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  if (!sortField) return rows;
  const actualField = ENROLLMENT_FIELD_ALIAS[sortField] || sortField;
  const dir = sortDirection === 'asc' ? 1 : -1;

  const out = [...rows];
  out.sort((a, b) => {
    const rawA = a[actualField];
    const rawB = b[actualField];

    // Date branch — missing values ALWAYS sink to the bottom regardless
    // of direction. Otherwise a desc sort on a sparse date column
    // would float all the empty rows to the top and bury actual dates.
    if (ENROLLMENT_DATE_FIELDS.has(actualField)) {
      const aHas = rawA != null && rawA !== '';
      const bHas = rawB != null && rawB !== '';
      if (!aHas && !bHas) return 0;
      if (!aHas) return 1;
      if (!bHas) return -1;
      const aTs = new Date(rawA).getTime();
      const bTs = new Date(rawB).getTime();
      if (Number.isNaN(aTs) && Number.isNaN(bTs)) return 0;
      if (Number.isNaN(aTs)) return 1;
      if (Number.isNaN(bTs)) return -1;
      return (aTs - bTs) * dir;
    }

    // Numeric branch
    if (NUMERIC_FIELDS.has(actualField)) {
      const aNum = parseFloat(rawA) || 0;
      const bNum = parseFloat(rawB) || 0;
      return (aNum - bNum) * dir;
    }

    // String fallback (case-insensitive)
    const aStr = String(rawA ?? '').toLowerCase();
    const bStr = String(rawB ?? '').toLowerCase();
    return aStr.localeCompare(bStr) * dir;
  });
  return out;
}
